import { createContext, useContext, useState, useMemo } from 'react';

const MonthContext = createContext(null);

/**
 * Session-only global month selection.
 * - Default = current calendar month on every page-load (per spec §1).
 * - Persists across in-app navigation but RESETS on browser refresh.
 * - Pages should read `year`/`month` from here and call `setMonth(y, m)` when the user picks a new period.
 */
export function MonthProvider({ children }) {
  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1).padStart(2, '0'));

  const value = useMemo(() => ({
    year,
    month,
    setMonth: (y, m) => {
      setYear(String(y));
      setMonth(String(m).padStart(2, '0'));
    },
    reset: () => {
      const d = new Date();
      setYear(String(d.getFullYear()));
      setMonth(String(d.getMonth() + 1).padStart(2, '0'));
    },
  }), [year, month]);

  return <MonthContext.Provider value={value}>{children}</MonthContext.Provider>;
}

export function useMonth() {
  const ctx = useContext(MonthContext);
  if (!ctx) throw new Error('useMonth must be used inside <MonthProvider>');
  return ctx;
}
